import { useState, type CSSProperties } from "react";
import { Pause, Play, Square, RotateCw } from "lucide-react";
import { Notify, toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useActiveRun, usePauseRun, useResumeRun } from "./hooks";
import { ProductionOrderFormDialog } from "@/features/production-orders/ProductionOrderFormDialog";
import { useAuthStore } from "@/stores/auth.store";
import { FinishProductionDialog } from "./FinishProductionDialog";
import { PauseRunDialog } from "./PauseRunDialog";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { formatRelativeTime } from "@/lib/display-labels";

interface Props {
  lineId: string;
  pollInterval?: number;
}

export function LineCommandBar({ lineId, pollInterval = 30_000 }: Props) {
  const activeRunQuery = useActiveRun(lineId, { refetchInterval: pollInterval });
  const { data: run, isLoading, isError, error, refetch } = activeRunQuery;
  const pause = usePauseRun(lineId);
  const resume = useResumeRun(lineId);
  // "Iniciar produção" abre o modal de Nova OP (linha travada). Criar OP é GESTOR-only.
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");
  const [showStart, setShowStart] = useState(false);
  const [showFinish, setShowFinish] = useState(false);
  const [showPause, setShowPause] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">Carregando…</CardContent>
      </Card>
    );
  }

  // Estado real desconhecido: NÃO pode cair no branch "sem produção em curso" (mostraria
  // "Iniciar produção" mesmo com uma run possivelmente ativa).
  if (isError) {
    return <Notify.QueryError error={error} onRetry={() => void refetch()} />;
  }

  if (!run) {
    return (
      <>
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="text-sm font-medium">Sem produção em curso</p>
              <p className="text-xs text-muted-foreground">
                Inicie uma execução para registrar produção, perdas e tempo parado.
              </p>
            </div>
            {isGestor ? (
              <Button onClick={() => setShowStart(true)}>
                <Play className="h-4 w-4" />
                Iniciar produção
              </Button>
            ) : null}
          </CardContent>
        </Card>
        {showStart ? (
          <ProductionOrderFormDialog
            mode="create"
            lockedLineId={lineId}
            onClose={() => setShowStart(false)}
          />
        ) : null}
      </>
    );
  }

  const progressPct = Math.min(100, (run.producedQty / Math.max(1, run.plannedQty)) * 100);
  const isActive = run.status === "ACTIVE";

  async function onPause(opts: { observation?: string } = {}) {
    try {
      await pause.mutateAsync({ runId: run!.id, observation: opts.observation });
      toast.success("Produção pausada.");
      setShowPause(false);
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao pausar.", { description: msg });
    }
  }

  async function onResume() {
    try {
      await resume.mutateAsync(run!.id);
      toast.success("Produção retomada.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao retomar.", { description: msg });
    }
  }

  return (
    <>
      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Em produção desde {formatRelativeTime(run.startedAt)}
              </p>
              <p className="text-sm font-medium">
                {run.products.map((p) => p.product.name).join(", ") || "Sem produtos"}
              </p>
              <p className="text-xs text-muted-foreground">
                <span className="font-mono font-medium">{run.producedQty}</span> /{" "}
                <span className="font-mono">{run.plannedQty}</span> unidades
                {run.responsible ? ` · ${run.responsible}` : null}
              </p>
            </div>
            <div className="flex gap-2">
              {isActive ? (
                <Button
                  variant="outline"
                  onClick={() => setShowPause(true)}
                  disabled={pause.isPending}
                >
                  <Pause className="h-4 w-4" />
                  Pausar
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => void onResume()}
                  disabled={resume.isPending}
                >
                  <RotateCw className="h-4 w-4" />
                  Retomar
                </Button>
              )}
              <Button onClick={() => setShowFinish(true)}>
                <Square className="h-4 w-4" />
                Finalizar
              </Button>
            </div>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full w-[var(--bar-w)] bg-primary transition-all"
              style={{ "--bar-w": `${progressPct}%` } as CSSProperties}
              aria-label={`Progresso ${progressPct.toFixed(0)}%`}
            />
          </div>
        </CardContent>
      </Card>

      <FinishProductionDialog
        lineId={lineId}
        runId={run.id}
        producedQty={run.producedQty}
        open={showFinish}
        onClose={() => setShowFinish(false)}
      />

      <PauseRunDialog
        open={showPause}
        onOpenChange={setShowPause}
        onConfirm={onPause}
        pending={pause.isPending}
        inputId="cmd-bar-pause-obs"
      />
    </>
  );
}
